import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  FaChevronDown, FaChevronUp, FaHome, FaServicestack, FaBlog,
  FaInfoCircle, FaChalkboardTeacher, FaCog, FaUserShield,
  FaEnvelopeOpenText, FaUserFriends, FaHandshake, FaTachometerAlt,
  FaUser, FaSignOutAlt, FaLock
} from 'react-icons/fa';
import Logo from '../../assets/logo.png';

const sidebarOptions = [
  {
    label: 'Dashboard',
    icon: <FaTachometerAlt className="w-5 h-5" />,
    children: [],
  },
  {
    label: 'Inquiry',
    icon: <FaEnvelopeOpenText className="w-5 h-5" />,
    children: [],
  },
  {
    label: 'Influencer Request',
    icon: <FaUserFriends className="w-5 h-5" />,
    children: [],
  },
  {
    label: 'Manage Influencers',
    icon: <FaUserFriends className="w-5 h-5" />,
    children: [],
  },
  {
    label: 'HireMeRequest',
    icon: <FaHandshake className="w-5 h-5" />,
    children: [],
  },
  {
    label: 'Content Edit',
    icon: <FaCog className="w-5 h-5" />,
    children: [
      { label: 'Homepage', icon: <FaHome className="w-4 h-4" /> },
      { label: 'Services', icon: <FaServicestack className="w-4 h-4" /> },
      { label: 'Blog', icon: <FaBlog className="w-4 h-4" /> },
      { label: 'About Us', icon: <FaInfoCircle className="w-4 h-4" /> },
      { label: 'UpSkill', icon: <FaChalkboardTeacher className="w-4 h-4" /> },
      { label: 'Premium Influencers', icon: <FaUserFriends className="w-4 h-4" /> },
    ],
  },
  {
    label: 'User Management',
    icon: <FaUserShield className="w-5 h-5" />,
    children: [
      { label: 'Admins' },
      { label: 'Users' },
    ],
  },
  {
    label: 'Settings',
    icon: <FaCog className="w-5 h-5" />,
    children: [],
  },
];

const AdminSidebar = ({ onSelect, activeOption: propActiveOption }) => {
  const [openDropdown, setOpenDropdown] = useState(null);
  const [activeOption, setActiveOption] = useState(() => {
    // Initialize from localStorage or prop or default to Dashboard
    return localStorage.getItem('adminSelectedPage') || propActiveOption || 'Dashboard';
  });
  const [adminInfo, setAdminInfo] = useState(null);
  const [adminRole, setAdminRole] = useState(() => localStorage.getItem('adminRole') || 'subadmin');
  const [allowedModules, setAllowedModules] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem('adminAllowedModules')) || [];
    } catch (e) {
      return [];
    }
  });
  const navigate = useNavigate();

  // Sync activeOption with prop changes
  useEffect(() => {
    if (propActiveOption) {
      setActiveOption(propActiveOption);
    }
  }, [propActiveOption]);

  // Load admin info from localStorage
  useEffect(() => {
    const adminEmail = localStorage.getItem('adminEmail');
    const adminName = localStorage.getItem('adminName');
    let role = localStorage.getItem('adminRole') || 'subadmin';
    if (adminEmail === 'admin1@gmail.com') {
      role = 'superadmin';
    }
    let modules = [];
    try {
      modules = JSON.parse(localStorage.getItem('adminAllowedModules')) || [];
    } catch (e) {}

    setAdminRole(role);
    setAllowedModules(modules);

    if (adminEmail) {
      setAdminInfo({
        email: adminEmail,
        name: adminName || 'Admin'
      });
    }
  }, []);

  const isAllowed = (label) => {
    const email = adminInfo?.email || localStorage.getItem('adminEmail');
    const isSuper = adminRole === 'superadmin' || email === 'admin1@gmail.com';
    if (isSuper) return true;
    if (label === 'Admins') return false; // Strictly superadmin only
    if (label === 'Dashboard') return true; // Always allow Dashboard
    return allowedModules.includes(label);
  };

  const handleDropdown = (idx) => {
    setOpenDropdown(openDropdown === idx ? null : idx);
  };

  const handleOptionClick = (label) => {
    setActiveOption(label);
    // Save to localStorage
    localStorage.setItem('adminSelectedPage', label);
    if (onSelect) onSelect(label);
  };

  const handleLogout = () => {
    // Clear admin session
    localStorage.removeItem('adminLoggedIn');
    localStorage.removeItem('adminEmail');
    localStorage.removeItem('adminName');
    localStorage.removeItem('adminSelectedPage');
    // Navigate to login
    navigate('/admin');
  };

  return (
    <aside className="h-screen w-64 bg-white border-r border-gray-200 flex flex-col fixed left-0 top-0 overflow-hidden">
      {/* Logo Section */}
      <div className="flex-shrink-0 p-6 border-b border-gray-200">
        <div className="flex items-center space-x-3">
          <img
            src={Logo}
            alt="CyborgSapient"
            className="h-12 w-auto max-w-full object-contain"
          />
          <div>
            <h1 className="text-xl font-bold text-gray-800">CyborgSapient</h1>
            <p className="text-sm text-gray-500">Admin Panel</p>
          </div>
        </div>
      </div>

      {/* Navigation - Scrollable */}
      <nav className="flex-1 overflow-y-auto p-4 space-y-2">
        {sidebarOptions.map((option, idx) => (
          <div key={option.label} className="space-y-1">
            {option.children.length === 0 ? (
              (() => {
                const allowed = isAllowed(option.label);
                return (
                  <button
                    disabled={!allowed}
                    className={`w-full flex items-center px-3 py-2.5 rounded-lg text-left transition-all duration-200 ${
                      !allowed
                        ? 'text-gray-300 cursor-not-allowed opacity-60'
                        : activeOption === option.label 
                          ? 'bg-gradient-to-r from-orange-50 via-pink-50 to-purple-50 text-orange-600 border border-orange-200' 
                          : 'text-gray-600 hover:bg-gray-50 hover:text-gray-800'
                    }`}
                    onClick={() => allowed && handleOptionClick(option.label)}
                  >
                    <span className="mr-3">{option.icon}</span>
                    <span className="font-medium flex-1">{option.label}</span>
                    {!allowed && <FaLock className="w-3.5 h-3.5 text-gray-400 ml-2 animate-pulse" />}
                  </button>
                );
              })()
            ) : (
              <>
                <button
                  className={`w-full flex items-center justify-between px-3 py-2.5 rounded-lg transition-all duration-200 ${
                    openDropdown === idx 
                      ? 'bg-gray-50 text-gray-800' 
                      : 'text-gray-600 hover:bg-gray-50 hover:text-gray-800'
                  }`}
                  onClick={() => handleDropdown(idx)}
                >
                  <div className="flex items-center">
                    <span className="mr-3">{option.icon}</span>
                    <span className="font-medium">{option.label}</span>
                  </div>
                  {openDropdown === idx ? <FaChevronUp className="w-3 h-3" /> : <FaChevronDown className="w-3 h-3" />}
                </button>
                {openDropdown === idx && (
                  <div className="ml-6 space-y-1">
                    {option.children.map(child => {
                      const childAllowed = isAllowed(child.label);
                      return (
                        <button
                          key={child.label}
                          disabled={!childAllowed}
                          className={`w-full flex items-center px-3 py-2 rounded-lg text-left transition-all duration-200 ${
                            !childAllowed
                              ? 'text-gray-300 cursor-not-allowed opacity-60'
                              : activeOption === child.label 
                                ? 'bg-gradient-to-r from-orange-50 via-pink-50 to-purple-50 text-orange-600 border border-orange-200' 
                                : 'text-gray-500 hover:bg-gray-50 hover:text-gray-700'
                          }`}
                          onClick={() => childAllowed && handleOptionClick(child.label)}
                        >
                          {child.icon && <span className="mr-3">{child.icon}</span>}
                          <span className="text-sm flex-1">{child.label}</span>
                          {!childAllowed && <FaLock className="w-3 h-3 text-gray-400 ml-2 animate-pulse" />}
                        </button>
                      );
                    })}
                  </div>
                )}
              </>
            )}
          </div>
        ))}
      </nav>

      {/* Footer with User Info and Logout - Fixed at bottom */}
      <div className="flex-shrink-0 border-t border-gray-200 bg-white">
        {adminInfo && (
          <div className="p-4 border-b border-gray-200">
            <div className="flex items-center space-x-3 mb-3">
              <div className="w-10 h-10 rounded-full bg-gradient-to-br from-orange-400 via-pink-400 to-purple-400 flex items-center justify-center text-white font-semibold">
                <FaUser className="w-5 h-5" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-gray-800 truncate">{adminInfo.name}</p>
                <p className="text-xs text-gray-500 truncate">{adminInfo.email}</p>
              </div>
            </div>
            <button
              onClick={handleLogout}
              className="w-full flex items-center justify-center space-x-2 px-4 py-2 bg-red-50 hover:bg-red-100 text-red-600 rounded-lg transition-colors duration-200 text-sm font-medium"
            >
              <FaSignOutAlt className="w-4 h-4" />
              <span>Logout</span>
            </button>
          </div>
        )}
        <div className="p-4">
          <div className="text-xs text-gray-500 text-center">
            © 2025 CyborgSapient Admin
          </div>
        </div>
      </div>
    </aside>
  );
};

export default AdminSidebar;
